import {
  fetchAbsolutePath,
  fetchJson,
  get,
  post,
  postJson,
} from '@/util/helpers/ajax-helpers';
import { completed, loading, standardErrorHandler } from './helpers';
import {
  COLUMNS_FOR_DATA_TABLE,
  COLUMNS_ROWS_LOADED,
  DATA_PRESENTATION_CHANGED,
  LAYER_USER_INTERACTION,
  MODAL_ROWS_LOADED,
  ROW_COUNT_LOADED,
  SHARED_ROWS_REQUEST_LOADED,
  TABLE_FULL_ROWS_LOADED,
  TABLE_ROWS_LOADED,
} from './constants';
import BadRequestException from '../util/BadRequestException';

const latestRequests = {};

export function rowsInvalidated(layers, loadingView, source, layerInEditMode) {
  const ACTION = source !== 'browser' ? MODAL_ROWS_LOADED : TABLE_ROWS_LOADED;
  return (dispatch, getState) => {
    $('#panel-tables').popover('hide');
    // request rows data
    const thisRequest = (latestRequests[source] + 1) | 1;
    latestRequests[source] = thisRequest;
    const page = getState().layers.page;
    const rowCount = getState().rows.totalRowCount; // this must go *before* ACTION
    const shareMetadataId = getState().rows.metadata?.currentMetadataId; // this must go *before* ACTION
    dispatch(loading(ACTION, { loadingView }));
    const params = ['includeHidden=true'];
    if (source === 'browser') {
      params.push('includeTemplateColumns=true');
    }
    if (layerInEditMode) {
      params.push(`layerInEditMode=${layerInEditMode.id()}`);
    }
    const { selectedView } = getState().layers;
    if (selectedView) {
      params.push(`selectedViewSeries=${selectedView._series}`);
    }
    postJson(`/columns?${params.join('&')}`, layers)
      .then((json) => {
        dispatch(completed(COLUMNS_FOR_DATA_TABLE, json));
        const rowsUrlParams = `page=${page}${
          shareMetadataId ? `&shareMetadataId=${shareMetadataId}` : ''
        }`;
        postJson(`/rows?${rowsUrlParams}`, layers)
          .then((_json) => {
            const latestRequest = latestRequests[source];
            if (thisRequest !== latestRequest) {
              return; // another request has already been sent, so throw this one away
            }
            const dt = _json._dataTable;
            dispatch(completed(ACTION, _json));
            // table loaded successfully, so now load row count
            if (page > 1) {
              // row count already loaded, so just re-use
              dispatch(
                completed(ROW_COUNT_LOADED, {
                  _rowCount: rowCount,
                }),
              );
            } else if (dt._rows.length < 100) {
              // all rows loaded, so just display
              dispatch(
                completed(ROW_COUNT_LOADED, {
                  _rowCount: dt._rows.length,
                }),
              );
            } else if (source === 'browser') {
              // need to ask for row count
              postJson('/rowCount', layers)
                .then((__json) => {
                  dispatch(completed(ROW_COUNT_LOADED, __json));
                })
                .catch(() => {
                  // just swallow.  Not the end of the world if this doesn't come back
                });
            }
          })
          .catch((error) => {
            if (error instanceof BadRequestException) {
              const latestRequest = latestRequests[source];
              if (thisRequest !== latestRequest) {
                return; // another request cancelled this one
              }
            }
            const errorHandler = standardErrorHandler(ACTION, dispatch);
            errorHandler(error);
          });
      })
      .catch(standardErrorHandler(COLUMNS_FOR_DATA_TABLE, dispatch));
  };
}

export function loadHtmlView(shareableSeries, pin) {
  return (dispatch) => {
    dispatch(loading(TABLE_ROWS_LOADED));
    fetchAbsolutePath(`/shared-rows/${shareableSeries}/${pin}`, true)
      .then((json) => {
        dispatch(completed(TABLE_ROWS_LOADED, json));
        // load chartConfig too
        dispatch(
          completed(LAYER_USER_INTERACTION, {
            event: 'update-charting-options',
            chartingOptions: json._chartConfig,
          }),
        );
      })
      .catch(standardErrorHandler(TABLE_ROWS_LOADED, dispatch));
  };
}

export function loadCachedResults() {
  return (dispatch, getState) => {
    const { lastLoadedView } = getState().views;
    postJson(`/cached-rows/${lastLoadedView.series()}`)
      .then((json) => {
        dispatch(completed(TABLE_ROWS_LOADED, json));
      })
      .catch(standardErrorHandler(TABLE_ROWS_LOADED, dispatch));
  };
}

export function loadRowRequest(rowRequestId) {
  return (dispatch, getState) => {
    const isNewWorld = getState().datasource.newWorld;
    dispatch(loading(SHARED_ROWS_REQUEST_LOADED));
    fetchJson(`/rows/${rowRequestId}/layers`)
      .then((json) => {
        dispatch(
          completed(SHARED_ROWS_REQUEST_LOADED, {
            json,
            isNewWorld,
          }),
        );
      })
      .catch(standardErrorHandler(SHARED_ROWS_REQUEST_LOADED, dispatch));
  };
}

let latestColumnsRequest = 0;

export function loadColumnsRows(layers) {
  latestColumnsRequest++;
  const thisRequest = latestColumnsRequest;
  return (dispatch) => {
    dispatch(loading(COLUMNS_ROWS_LOADED));
    postJson('/columnRows', layers)
      .then((json) => {
        if (thisRequest === latestColumnsRequest) {
          dispatch(completed(COLUMNS_ROWS_LOADED, { json }));
        }
      })
      .catch(standardErrorHandler(COLUMNS_ROWS_LOADED, dispatch));
  };
}

export function changeDataPresentation(presentation) {
  return completed(DATA_PRESENTATION_CHANGED, { presentation });
}

export function dataTableInteraction(props) {
  return completed(props.type, props.payload);
}

export function dataTableFullResults(rowRequestId) {
  return (dispatch) => {
    const path = `/rows/${rowRequestId}/full-results`;
    dispatch(loading(TABLE_FULL_ROWS_LOADED));
    post('Retrieving more results', path)
      .then((json) => {
        pollDataTableFullResults(rowRequestId, json._series, dispatch);
      })
      .catch(standardErrorHandler(TABLE_FULL_ROWS_LOADED, dispatch));
  };
}

const MAX_POLL_DELAY_MS = 10000;

function pollDataTableFullResults(
  rowRequestId,
  series,
  dispatch,
  delayMs = 200,
) {
  const path = `/cached-rows/${series}/full-results`;

  get('Checking if more results are ready', path)
    .then((json) => {
      if (json._rows && json._rows.length > 0) {
        dispatch(
          completed(TABLE_FULL_ROWS_LOADED, {
            _rowRequestId: rowRequestId,
            _dataTable: json,
            _allResults: true,
          }),
        );
      } else {
        setTimeout(
          () =>
            pollDataTableFullResults(
              rowRequestId,
              series,
              dispatch,
              Math.min(2 * delayMs, MAX_POLL_DELAY_MS),
            ),
          delayMs,
        );
      }
    })
    .catch(standardErrorHandler(TABLE_FULL_ROWS_LOADED, dispatch));
}
